import { useEffect, useMemo, useState } from "react";
import InfoTooltip from "../common/InfoTooltip";

const METRICS = [
  {
    key: "daily_revenue",
    field: "target_daily_revenue",
    label: "Daily Revenue",
    helper: "Current month average revenue per day compared to the assigned target.",
    unit: "currency",
    step: "0.01",
    tooltip: "Daily sales target for this staff member.",
  },
  {
    key: "revenue_per_minute",
    field: "target_revenue_per_minute",
    label: "Revenue per Minute",
    helper: "Current month net sales divided by booked minutes.",
    unit: "currency_per_minute",
    step: "0.01",
    tooltip: "Sales target for each booked minute.",
  },
  {
    key: "utilization",
    field: "target_utilization_percent",
    label: "Utilization",
    helper: "Booked hours divided by available hours for the current month.",
    unit: "percent",
    step: "0.1",
    tooltip: "How much of available time should be booked.",
  },
  {
    key: "rebooking",
    field: "target_rebooking_percent",
    label: "Rebooking",
    helper: "Rebooked clients divided by served clients for the current month.",
    unit: "percent",
    step: "0.1",
    tooltip: "Percent of clients expected to rebook.",
  },
];

const normalizeValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : "";
};

const toNullableNumber = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatValue = (value, unit) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed)) {
    if (unit === "currency" || unit === "currency_per_minute") {
      return unit === "currency_per_minute" ? "$0.00/min" : "$0.00";
    }
    return "0%";
  }
  if (unit === "currency") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parsed);
  }
  if (unit === "currency_per_minute") {
    return `${new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parsed)}/min`;
  }
  return `${parsed.toFixed(1)}%`;
};

const toneForAchievement = (value) => {
  if (value === null || value === undefined) {
    return {
      pill: "bg-slate-100 text-slate-600",
      bar: "bg-slate-300",
      label: "Target not set",
    };
  }
  if (value >= 100) {
    return {
      pill: "bg-emerald-100 text-emerald-700",
      bar: "bg-emerald-500",
      label: `${value.toFixed(0)}% of target`,
    };
  }
  if (value >= 75) {
    return {
      pill: "bg-amber-100 text-amber-700",
      bar: "bg-amber-500",
      label: `${value.toFixed(0)}% of target`,
    };
  }
  return {
    pill: "bg-rose-100 text-rose-700",
    bar: "bg-rose-500",
    label: `${value.toFixed(0)}% of target`,
  };
};

export default function StaffKpiProgressCard({
  title = "Targeted KPI",
  subtitle = "Track current month progress against assigned KPI targets.",
  cardClass = "rounded-3xl border border-slate-200 bg-white p-6 shadow-sm",
  progress = null,
  loading = false,
  error = "",
  editable = false,
  initialTargets = {},
  onSaveTargets,
  saving = false,
}) {
  const [draftTargets, setDraftTargets] = useState({
    target_daily_revenue: "",
    target_revenue_per_minute: "",
    target_utilization_percent: "",
    target_rebooking_percent: "",
  });

  useEffect(() => {
    setDraftTargets({
      target_daily_revenue: normalizeValue(initialTargets?.target_daily_revenue),
      target_revenue_per_minute: normalizeValue(
        initialTargets?.target_revenue_per_minute
      ),
      target_utilization_percent: normalizeValue(
        initialTargets?.target_utilization_percent
      ),
      target_rebooking_percent: normalizeValue(
        initialTargets?.target_rebooking_percent
      ),
    });
  }, [
    initialTargets?.target_daily_revenue,
    initialTargets?.target_revenue_per_minute,
    initialTargets?.target_utilization_percent,
    initialTargets?.target_rebooking_percent,
  ]);

  const hasAnyTarget = useMemo(
    () =>
      METRICS.some((metric) => {
        const metricPayload = progress?.metrics?.[metric.key];
        return metricPayload?.target !== null && metricPayload?.target !== undefined;
      }),
    [progress]
  );

  const handleTargetChange = (field, value) => {
    setDraftTargets((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (typeof onSaveTargets !== "function") return;
    await onSaveTargets({
      target_daily_revenue: toNullableNumber(draftTargets.target_daily_revenue),
      target_revenue_per_minute: toNullableNumber(
        draftTargets.target_revenue_per_minute
      ),
      target_utilization_percent: toNullableNumber(
        draftTargets.target_utilization_percent
      ),
      target_rebooking_percent: toNullableNumber(
        draftTargets.target_rebooking_percent
      ),
    });
  };

  return (
    <div className={cardClass}>
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
            <InfoTooltip
              text="Set staff goals and compare them with this month's results."
              label={title}
              maxWidth={280}
            />
          </div>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        </div>
        {progress?.available !== false && progress?.period_label ? (
          <div className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
            {progress.period_label}
            <InfoTooltip
              text="Date range used for these KPI results."
              label="KPI period"
              maxWidth={240}
            />
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="mt-4 text-sm text-slate-500">Loading KPI progress...</div>
      ) : error ? (
        <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : progress?.available === false ? (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          {progress?.message || "KPI progress is not available yet."}
        </div>
      ) : (
        <>
          {!hasAnyTarget && (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              No KPI targets have been assigned yet. Current month actuals are shown below.
            </div>
          )}

          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {METRICS.map((metric) => {
              const metricPayload = progress?.metrics?.[metric.key] || {};
              const targetValue = metricPayload?.target;
              const actualValue = metricPayload?.actual || 0;
              const achievement = metricPayload?.achievement_percent;
              const tone = toneForAchievement(achievement);
              const progressWidth =
                achievement === null || achievement === undefined
                  ? 0
                  : Math.max(0, Math.min(100, achievement));

              return (
                <div
                  key={metric.key}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
                        {metric.label}
                        <InfoTooltip
                          text={metric.tooltip}
                          label={metric.label}
                          maxWidth={260}
                        />
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{metric.helper}</p>
                    </div>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${tone.pill}`}
                    >
                      {tone.label}
                    </span>
                  </div>

                  <div className="mt-4 space-y-3">
                    <div>
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Target
                        <InfoTooltip
                          text="Goal entered for this staff member."
                          label={`${metric.label} target`}
                          maxWidth={240}
                        />
                      </div>
                      {editable ? (
                        <input
                          type="number"
                          min="0"
                          step={metric.step}
                          value={draftTargets[metric.field]}
                          onChange={(event) =>
                            handleTargetChange(metric.field, event.target.value)
                          }
                          className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                          placeholder={
                            metric.unit === "percent" ? "0.0" : "0.00"
                          }
                        />
                      ) : (
                        <div className="mt-1 text-lg font-semibold text-slate-900">
                          {targetValue === null || targetValue === undefined
                            ? "Not set"
                            : formatValue(targetValue, metric.unit)}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Actual
                        <InfoTooltip
                          text="Current result for this month."
                          label={`${metric.label} actual`}
                          maxWidth={240}
                        />
                      </div>
                      <div className="mt-1 text-lg font-semibold text-slate-900">
                        {formatValue(actualValue, metric.unit)}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-400">
                        <span className="flex items-center gap-2">
                          Achievement
                          <InfoTooltip
                            text="Actual result compared with the target."
                            label={`${metric.label} achievement`}
                            maxWidth={250}
                          />
                        </span>
                        <span>
                          {achievement === null || achievement === undefined
                            ? "Not set"
                            : `${achievement.toFixed(0)}%`}
                        </span>
                      </div>
                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
                        <div
                          className={`h-full rounded-full ${tone.bar}`}
                          style={{ width: `${progressWidth}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {editable ? (
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-full bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-70"
              >
                {saving ? "Saving..." : "Save KPI Targets"}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
